import { useState } from "react";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { ViewToggle } from "@/components/transportation/ViewToggle";
import { TransportationFilters } from "@/components/transportation/TransportationFilters";
import { useCountries } from "@/hooks/useCountries";
import { useCities } from "@/hooks/useCities";
import { useAirportsForFilters } from "@/hooks/useAirports";

interface AirportTransfer {
  id: string;
  from_location: string;
  to_location: string;
  description_ar: string;
  vehicle_type_id: string;
  price: number;
  currency: string;
  created_at: string;
  vehicle_types: { name_ar: string };
  country_id: string | null;
  city_id: string | null;
  airport_id: string | null;
}

interface VehicleType {
  id: string;
  name_ar: string;
}

export default function AirportTransfers() {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<AirportTransfer | null>(null);
  const [view, setView] = useState<'table' | 'cards'>('cards');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [filterBy, setFilterBy] = useState('all');
  const [countryId, setCountryId] = useState('all');
  const [cityId, setCityId] = useState('all');
  const [airportId, setAirportId] = useState('all');
  
  const [formData, setFormData] = useState({
    from_location: "",
    to_location: "",
    description_ar: "",
    vehicle_type_id: "",
    price: 0,
    currency: "SAR",
    country_id: "",
    city_id: "",
    airport_id: "",
  });
  const queryClient = useQueryClient();

  const { data: countries = [] } = useCountries();
  const { data: cities = [] } = useCities(countryId !== 'all' ? countryId : undefined);
  const { data: airportsForFilter = [] } = useAirportsForFilters({ 
    cityId: cityId !== 'all' ? cityId : undefined,
    countryId: countryId !== 'all' ? countryId : undefined,
  });

  // For dialog dependent dropdowns
  const [dialogCountryId, setDialogCountryId] = useState<string | undefined>();
  const [dialogCityId, setDialogCityId] = useState<string | undefined>();
  const { data: dialogCities = [] } = useCities(dialogCountryId);
  const { data: dialogAirports = [] } = useAirportsForFilters({ cityId: dialogCityId });

  const { data: vehicleTypes = [] } = useQuery<VehicleType[]>({
    queryKey: ['vehicle_types'],
    queryFn: async () => {
      const { data, error } = await supabase.from('vehicle_types').select('id, name_ar').order('sort_order');
      if (error) throw error;
      return data;
    },
  });

  const { data: transfers = [], isLoading } = useQuery({
    queryKey: ['airport-transfers'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('airport_transfers')
        .select(`
          *,
          vehicle_types(name_ar)
        `)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data as AirportTransfer[];
    }
  });

  // فلترة وترتيب البيانات
  const filteredAndSortedTransfers = transfers
    .filter(transfer => {
      const matchesSearch = transfer.from_location.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           transfer.to_location.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (transfer.description_ar && transfer.description_ar.toLowerCase().includes(searchTerm.toLowerCase()));
      
      const matchesCountry = countryId === 'all' || !transfer.country_id || transfer.country_id === countryId;
      const matchesCity = cityId === 'all' || !transfer.city_id || transfer.city_id === cityId;
      const matchesAirport = airportId === 'all' || !transfer.airport_id || transfer.airport_id === airportId;

      const baseFilter = matchesSearch && matchesCountry && matchesCity && matchesAirport;
      
      if (filterBy === 'has_price') return baseFilter && transfer.price > 0;
      if (filterBy === 'no_price') return baseFilter && (!transfer.price || transfer.price === 0);
      return baseFilter;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'from_location':
          return a.from_location.localeCompare(b.from_location, 'ar');
        case 'from_location_desc':
          return b.from_location.localeCompare(a.from_location, 'ar');
        case 'created_at':
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        case 'created_at_desc':
          return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
        default:
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      }
    });

  const createMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const { error } = await supabase
        .from('airport_transfers')
        .insert([data]);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['airport-transfers'] });
      toast.success("تم إضافة خدمة الاستقبال والتوديع بنجاح");
      resetForm();
    },
    onError: (error) => {
      toast.error("حدث خطأ أثناء إضافة الخدمة");
      console.error(error);
    }
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<typeof formData> }) => {
      const { error } = await supabase
        .from('airport_transfers')
        .update(data)
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['airport-transfers'] });
      toast.success("تم تحديث الخدمة بنجاح");
      resetForm();
    },
    onError: (error) => {
      toast.error("حدث خطأ أثناء تحديث الخدمة");
      console.error(error);
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('airport_transfers')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['airport-transfers'] });
      toast.success("تم حذف الخدمة بنجاح");
    },
    onError: (error) => {
      toast.error("حدث خطأ أثناء حذف الخدمة");
      console.error(error);
    }
  });

  const resetForm = () => {
    setFormData({
      from_location: "",
      to_location: "",
      description_ar: "",
      vehicle_type_id: "",
      price: 0,
      currency: "SAR",
      country_id: "",
      city_id: "",
      airport_id: "",
    });
    setEditingItem(null);
    setIsDialogOpen(false);
    setDialogCountryId(undefined);
    setDialogCityId(undefined);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const dataToSubmit = {
        ...formData,
        vehicle_type_id: formData.vehicle_type_id || null,
        country_id: formData.country_id || null,
        city_id: formData.city_id || null,
        airport_id: formData.airport_id || null,
    };
    
    if (editingItem) {
      updateMutation.mutate({ id: editingItem.id, data: dataToSubmit });
    } else {
      createMutation.mutate(dataToSubmit);
    }
  };

  const handleEdit = (item: AirportTransfer) => {
    setEditingItem(item);
    setDialogCountryId(item.country_id || undefined);
    setDialogCityId(item.city_id || undefined);
    setFormData({
      from_location: item.from_location,
      to_location: item.to_location,
      description_ar: item.description_ar || "",
      vehicle_type_id: item.vehicle_type_id || "",
      price: item.price || 0,
      currency: item.currency || "SAR",
      country_id: item.country_id || "",
      city_id: item.city_id || "",
      airport_id: item.airport_id || "",
    });
    setIsDialogOpen(true);
  };

  const handleDelete = (id: string) => {
    if (confirm("هل أنت متأكد من حذف هذه الخدمة؟")) {
      deleteMutation.mutate(id);
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSortBy('created_at');
    setFilterBy('all');
    setCountryId('all');
    setCityId('all');
    setAirportId('all');
  };

  if (isLoading) {
    return <div className="flex justify-center p-8">جاري التحميل...</div>;
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">الاستقبال والتوديع</h1>
          <p className="text-gray-600 mt-1">إدارة خدمات الاستقبال والتوديع من وإلى المطار</p>
        </div>
        <div className="flex gap-3">
          <ViewToggle view={view} onViewChange={setView} />
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => resetForm()}>
                <Plus className="w-4 h-4 mr-2" />
                إضافة خدمة جديدة
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>
                  {editingItem ? "تعديل الخدمة" : "إضافة خدمة جديدة"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="country_id">الدولة</Label>
                    <Select value={formData.country_id} onValueChange={(value) => {
                      setFormData({ ...formData, country_id: value, city_id: "", airport_id: "", from_location: "" });
                      setDialogCountryId(value);
                      setDialogCityId(undefined);
                    }}>
                      <SelectTrigger><SelectValue placeholder="اختر الدولة" /></SelectTrigger>
                      <SelectContent>
                        {countries.map(c => <SelectItem key={c.id} value={c.id}>{c.name_ar}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="city_id">المدينة</Label>
                    <Select value={formData.city_id} onValueChange={(value) => {
                      setFormData({ ...formData, city_id: value, airport_id: "", from_location: "" });
                      setDialogCityId(value);
                    }} disabled={!dialogCountryId || !dialogCities.length}>
                      <SelectTrigger><SelectValue placeholder="اختر المدينة" /></SelectTrigger>
                      <SelectContent>
                        {dialogCities.map(c => <SelectItem key={c.id} value={c.id}>{c.name_ar}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label htmlFor="airport_id">المطار</Label>
                  <Select value={formData.airport_id} onValueChange={(value) => {
                    const airportName = dialogAirports?.find(a => a.id === value)?.name_ar || "";
                    setFormData({ ...formData, airport_id: value, from_location: airportName });
                  }} disabled={!dialogCityId || !dialogAirports || !dialogAirports.length}>
                    <SelectTrigger><SelectValue placeholder="اختر المطار" /></SelectTrigger>
                    <SelectContent>
                      {dialogAirports?.map(a => <SelectItem key={a.id} value={a.id}>{a.name_ar} ({a.iata_code})</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="from_location">من (تلقائي)</Label>
                    <Input
                      id="from_location"
                      value={formData.from_location}
                      readOnly
                      disabled
                      placeholder="يتم تحديده من المطار"
                    />
                  </div>
                  <div>
                    <Label htmlFor="to_location">إلى</Label>
                    <Input
                      id="to_location"
                      value={formData.to_location}
                      onChange={(e) => setFormData({ ...formData, to_location: e.target.value })}
                      required
                      placeholder="الوجهة"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="description_ar">الوصف</Label>
                  <Textarea
                    id="description_ar"
                    value={formData.description_ar}
                    onChange={(e) => setFormData({ ...formData, description_ar: e.target.value })}
                    placeholder="وصف الخدمة"
                  />
                </div>
                <div>
                  <Label htmlFor="vehicle_type_id">نوع السيارة</Label>
                  <Select value={formData.vehicle_type_id} onValueChange={(value) => setFormData({ ...formData, vehicle_type_id: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="اختر نوع السيارة" />
                    </SelectTrigger>
                    <SelectContent>
                      {vehicleTypes.map((type) => (
                        <SelectItem key={type.id} value={type.id}>
                          {type.name_ar}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="price">السعر</Label>
                    <Input
                      id="price"
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.price}
                      onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) })}
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <Label htmlFor="currency">العملة</Label>
                    <Select value={formData.currency} onValueChange={(value) => setFormData({ ...formData, currency: value })}>
                      <SelectTrigger>
                        <SelectValue placeholder="اختر العملة" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="SAR">ريال سعودي</SelectItem>
                        <SelectItem value="USD">دولار أمريكي</SelectItem>
                        <SelectItem value="EUR">يورو</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={resetForm}>
                    إلغاء
                  </Button>
                  <Button type="submit">
                    {editingItem ? "تحديث" : "إضافة"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <TransportationFilters
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        sortBy={sortBy}
        onSortChange={setSortBy}
        filterBy={filterBy}
        onFilterChange={setFilterBy}
        onClearFilters={clearFilters}
        countryId={countryId}
        onCountryChange={(value) => { setCountryId(value); setCityId('all'); setAirportId('all'); }}
        countries={countries}
        cityId={cityId}
        onCityChange={(value) => { setCityId(value); setAirportId('all'); }}
        cities={cities || []}
        airportId={airportId}
        onAirportChange={setAirportId}
        airports={airportsForFilter || []}
      />

      {view === 'table' ? (
        <div className="border rounded-lg bg-white">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>من</TableHead>
                <TableHead>إلى</TableHead>
                <TableHead>نوع السيارة</TableHead>
                <TableHead>السعر</TableHead>
                <TableHead>الوصف</TableHead>
                <TableHead>تاريخ الإنشاء</TableHead>
                <TableHead>العمليات</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredAndSortedTransfers.map((transfer) => (
                <TableRow key={transfer.id}>
                  <TableCell className="font-medium">{transfer.from_location}</TableCell>
                  <TableCell className="font-medium">{transfer.to_location}</TableCell>
                  <TableCell>{transfer.vehicle_types?.name_ar || '-'}</TableCell>
                  <TableCell>
                    {transfer.price ? (
                      <span className="font-medium">{transfer.price} {transfer.currency}</span>
                    ) : (
                      <span className="text-gray-500">غير محدد</span>
                    )}
                  </TableCell>
                  <TableCell className="max-w-xs truncate" title={transfer.description_ar}>
                    {transfer.description_ar || '-'}
                  </TableCell>
                  <TableCell className="text-gray-500">
                    {new Date(transfer.created_at).toLocaleDateString('ar-SA')}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => handleEdit(transfer)}>
                        تعديل
                      </Button>
                      <Button 
                        size="sm" 
                        variant="destructive" 
                        onClick={() => handleDelete(transfer.id)}
                      >
                        حذف
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredAndSortedTransfers.map((transfer) => (
            <Card key={transfer.id} className="hover:shadow-md transition-shadow">
              <CardHeader>
                <CardTitle className="flex flex-col gap-2">
                  <div className="flex items-center justify-between">
                    <span className="text-lg">{transfer.from_location}</span>
                    <span className="text-sm text-gray-500">→</span>
                    <span className="text-lg">{transfer.to_location}</span>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {transfer.description_ar && (
                    <p className="text-sm text-gray-500 line-clamp-2">{transfer.description_ar}</p>
                  )}
                  <div className="flex justify-between items-center">
                    <span><strong>نوع السيارة:</strong></span>
                    <span>{transfer.vehicle_types?.name_ar || 'غير محدد'}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span><strong>السعر:</strong></span>
                    {transfer.price ? (
                      <span className="text-green-600 font-medium">{transfer.price} {transfer.currency}</span>
                    ) : (
                      <span className="text-gray-500">غير محدد</span>
                    )}
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span><strong>تاريخ الإنشاء:</strong></span>
                    <span className="text-gray-500">{new Date(transfer.created_at).toLocaleDateString('ar-SA')}</span>
                  </div>
                </div>
                <div className="flex gap-2 mt-4">
                  <Button size="sm" onClick={() => handleEdit(transfer)} className="flex-1">
                    تعديل
                  </Button>
                  <Button 
                    size="sm" 
                    variant="destructive" 
                    onClick={() => handleDelete(transfer.id)}
                    className="flex-1"
                  >
                    حذف
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {filteredAndSortedTransfers.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500">لا توجد خدمات متاحة حالياً</p>
        </div>
      )}
    </div>
  );
}
